import React from 'react';
import { Box, FormControlLabel, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';

import { JsonInput } from '@shared/components/JsonInput';
import CustomSwitch from '@shared/components/forms/theme-elements/CustomSwitch';

import { NoiseGuardDraft, NoiseGuardValidation } from '../utils/campaignNoiseProcessing.utils';

type NoiseGuardFormProps = {
  value: NoiseGuardDraft;
  onChange: (value: NoiseGuardDraft) => void;
  errors?: NoiseGuardValidation;
  disabled?: boolean;
  showEnabledToggle?: boolean;
};

export const NoiseGuardForm: React.FC<NoiseGuardFormProps> = ({
  value,
  onChange,
  errors = {},
  disabled = false,
  showEnabledToggle = true,
}) => {
  const { t } = useTranslation();
  const fieldsDisabled = disabled || !value.enabled;

  return (
    <Box
      sx={{
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
        opacity: fieldsDisabled ? 0.7 : 1,
      }}>
      {showEnabledToggle ? (
        <FormControlLabel
          control={
            <CustomSwitch
              checked={value.enabled}
              disabled={disabled}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                onChange({ ...value, enabled: event.target.checked })
              }
            />
          }
          label={t('noiseProcessing.guard.enabled')}
          sx={{ ml: 0, gap: 1 }}
        />
      ) : null}
      <FormControlLabel
        control={
          <CustomSwitch
            checked={value.shadowMode}
            disabled={fieldsDisabled}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
              onChange({ ...value, shadowMode: event.target.checked })
            }
          />
        }
        label={t('noiseProcessing.guard.shadowMode')}
        sx={{ ml: 0, gap: 1 }}
      />
      <Typography variant="body2" color="text.secondary">
        {t('noiseProcessing.guard.shadowModeHelper')}
      </Typography>
      <FormControlLabel
        control={
          <CustomSwitch
            checked={value.hangupEnabled}
            disabled={fieldsDisabled}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
              onChange({ ...value, hangupEnabled: event.target.checked })
            }
          />
        }
        label={t('noiseProcessing.guard.hangupEnabled')}
        sx={{ ml: 0, gap: 1 }}
      />
      <Typography variant="body2" color="text.secondary">
        {t('noiseProcessing.guard.hangupEnabledHelper')}
      </Typography>
      <Typography variant="body2" color="text.secondary">
        {t('noiseProcessing.guard.triggers')}
      </Typography>
      <Typography variant="body2" color="text.secondary">
        {t('noiseProcessing.guard.triggersDescription')}
      </Typography>
      <Box aria-live="polite" aria-atomic="true">
        <JsonInput
          value={value.triggers}
          onChange={(triggers) => onChange({ ...value, triggers })}
          error={errors.triggers ? t(errors.triggers) : undefined}
          disabled={fieldsDisabled}
          rows={5}
          placeholder={t('noiseProcessing.jsonPlaceholder')}
        />
      </Box>
    </Box>
  );
};
